import { useSyncExternalStore } from "react";

/**
 * Which shell is on screen: the desk's columns, or the phone's one pane under a bottom
 * bar.
 *
 * ## Why this asks the stylesheet instead of holding the number
 *
 * A `matchMedia` breakpoint here would put it in two places, `mosaic.css` deciding the
 * layout and this deciding behaviour that must match it, and nothing fails when the two
 * drift. So the stylesheet declares `--shell` beside the media query, and this reads back
 * the answer it already gave. The value is cached and recomputed only on resize.
 *
 * Under test there is no stylesheet, the property reads empty, and the answer is `desk`:
 * the layout with no fixed chrome, so nothing is hidden behind furniture that is not
 * there.
 */
export type ShellLayout = "desk" | "phone";

/**
 * The shell layout currently on screen, re-rendering the caller when a resize changes it.
 *
 * @returns `desk` or `phone`; `desk` when the stylesheet declares neither.
 */
export function useShellLayout(): ShellLayout {
  return useSyncExternalStore(subscribe, snapshot, desk);
}

let cached: ShellLayout | null = null;
const listeners = new Set<() => void>();

/**
 * The layout the stylesheet declares now, read uncached.
 *
 * @internal
 * @returns `phone` when the stylesheet says so, else `desk`.
 */
function read(): ShellLayout {
  const declared = getComputedStyle(document.documentElement).getPropertyValue("--shell").trim();
  return declared === "phone" ? "phone" : "desk";
}

/**
 * `useSyncExternalStore` calls this on every render and requires the same value back
 * until the store says otherwise, so the read is cached.
 *
 * @internal
 */
function snapshot(): ShellLayout {
  if (cached === null) cached = read();
  return cached;
}

/**
 * Register a listener for layout changes, for `useSyncExternalStore`.
 *
 * @internal
 * @param onStoreChange Called after a resize changes the layout.
 * @returns A function that removes the listener.
 */
function subscribe(onStoreChange: () => void): () => void {
  // A resize while nothing was subscribed leaves the cache stale; re-reading on the way
  // in costs one property read and React re-checks the snapshot immediately after.
  cached = read();
  listeners.add(onStoreChange);
  if (listeners.size === 1) window.addEventListener("resize", onResize);
  return () => {
    listeners.delete(onStoreChange);
    if (listeners.size === 0) window.removeEventListener("resize", onResize);
  };
}

/**
 * Refresh the cached layout and notify listeners if it changed.
 *
 * @internal
 */
function onResize(): void {
  const next = read();
  if (next === cached) return;
  cached = next;
  for (const listener of listeners) listener();
}

/**
 * The server snapshot for `useSyncExternalStore`: always `desk`.
 *
 * @internal
 * @returns `desk`.
 */
function desk(): ShellLayout {
  return "desk";
}
